import { pushVideoEvent, VIDEO } from "./events";

// Youtube
export function embedYtScript(ytVideo, config) {
    let ytIframeAPI = "https://www.youtube.com/iframe_api";
    if (typeof window.YT == "undefined" || typeof window.YT.Player == "undefined") {
        let tag = document.createElement("script");
        tag.src = ytIframeAPI;
        let firstScriptTag = document.querySelector("script");
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

        if (Array.isArray(window.ytIframes)) {
            window.ytIframes.push({ ytVideo, config });
        } else {
            window.ytIframes = [{ ytVideo, config }];
        }

        window.onYouTubePlayerAPIReady = function () {
            window.ytIframes.forEach(ytIframe => onYouTubeReady(ytIframe.ytVideo, ytIframe.config));
        };
    } else {
        onYouTubeReady(ytVideo, config);
    }
}

export function onYouTubeReady(ytVideo, config) {
    let id = ytVideo.getAttribute("id");
    let videoId = ytVideo.dataset.videoid;
    let title = ytVideo.dataset.title;
    let context = ytVideo.dataset.gtmcontext;
    let carousel = ytVideo.closest(".carousel__wrap");
    let eventsPushed = {};
    let timer = null;
    const isLooping = config?.loop === 1 || config?.loop === "1";

    const onApiChange = () => {
        if (typeof player.setOption === 'function' && config.cc_load_policy === "1") {
            player.setOption('captions', 'track', { languageCode: config.cc_lang_pref })
        }
    }

    const convertTime = time => {
        const splitTime = time.split(":");
        const arrLen = splitTime.length;
        let hh = 0, mm = 0, ss = 0;

        if (arrLen === 3) {
            hh = Number(splitTime[0]) * 3600;
            mm = Number(splitTime[1]) * 60;
            ss = Number(splitTime[2]);
        } else if (arrLen === 2) {
            mm = Number(splitTime[0]) * 60;
            ss = Number(splitTime[1]);
        } else {
            ss = Number(splitTime[0]);
        }

        return hh + mm + ss;
    }

    if (config.start) config.start = convertTime(config?.start);
    if (config.end) config.end = convertTime(config?.end);

    const [videoIdNoParam] = videoId.split("?");

    if (config.playlist) config.playlist = videoIdNoParam;

    let player = new window.YT.Player(id, {
        videoId: videoIdNoParam,
        title,
        host: 'https://www.youtube-nocookie.com',
        playerVars: {
            playsinline: 1,
            ...config
        },
        events: {
            onStateChange: onPlayerStateChange,
            onError: onPlayerError,
            onApiChange,
        },
    });

    player.addEventListener("onReady", (e) => {
        const duration = e.target.playerInfo.duration;

        if (duration === 0) {
            pushVideoEvent({
                event: VIDEO.error,
                video_duration: 0,
                video_percent: "0%",
                video_provider: "youtube",
                error_type: "video not found",
            });
            eventsPushed[VIDEO.error] = true;
        } else {
            if (ytVideo.closest(".tns-slide-cloned")) {
                eventsPushed[VIDEO.view] = true;
            } else if (!eventsPushed[VIDEO.view]) {
                pushVideoEvent({
                    event: VIDEO.view,
                    video_duration: duration,
                    video_percent: "0%",
                    video_provider: "youtube",
                });
                eventsPushed[VIDEO.view] = true;
            }
        }
    });

    function onPlayerStateChange(event) {
        const duration = event.target.playerInfo.duration;
        const state = event.data; // reliable source per YT Iframe API

        if (state === window.YT.PlayerState.PLAYING) {
            if (!eventsPushed[VIDEO.start]) {
                pushVideoEvent({
                    event: VIDEO.start,
                    video_duration: duration,
                    video_percent: "0%",
                    video_provider: "youtube",
                });
                eventsPushed[VIDEO.start] = true;
            }

            if (timer) clearInterval(timer);

            // Periodic milestone tracking (and loop detection)
            timer = setInterval(() => {
                const currentTime = player.playerInfo.currentTime;
                const percent = Math.floor((currentTime / duration) * 100);

                // Progress milestones (fire once per play; reset on loop start)
                const milestones = [40, 80, 100];
                milestones.forEach(m => {
                    const key = `progress_${m}`;
                    if (percent >= m && !eventsPushed[key]) {
                        eventsPushed[key] = true;
                        pushVideoEvent({
                            event: VIDEO.progress(m),
                            video_duration: duration,
                            video_percent: `${m}%`,
                            video_provider: "youtube",
                        });
                    }
                });

                // Emulate loop complete detection
                if (isLooping) {
                    if (percent >= 90 && !eventsPushed["loop_finish"]) {
                        eventsPushed["loop_finish"] = true;
                        pushVideoEvent({
                            event: VIDEO.finish,
                            video_duration: duration,
                            video_percent: "100%",
                            video_provider: "youtube",
                        });
                    }

                    // Reset progress markers near loop restart
                    if (percent < 5) {
                        [10, 25, 50, 75, 90].forEach(m => { eventsPushed[`progress_${m}`] = false; });
                        eventsPushed["loop_finish"] = false;
                    }
                }
            }, 1000);
        } else if (state === window.YT.PlayerState.ENDED) {
            // Fire once only, if not looping
            if (!isLooping) {
                pushVideoEvent({
                    event: VIDEO.finish,
                    video_duration: duration,
                    video_percent: "100%",
                    video_provider: "youtube",
                });
                eventsPushed = {};
            }

            if (timer) clearInterval(timer);
        } else if (state === window.YT.PlayerState.PAUSED) {
            const currentTime = event.target.getCurrentTime();
            const videoPercent = Math.floor((currentTime / duration) * 100);
            pushVideoEvent({
                event: VIDEO.progress(videoPercent),
                video_duration: duration,
                video_percent: `${videoPercent}%`,
                video_provider: "youtube",
            });
            if (timer) clearInterval(timer);
        }
    }

    function onPlayerError(event) {
        if (timer) clearInterval(timer);

        if (!eventsPushed[VIDEO.error]) {
            pushVideoEvent({
                event: VIDEO.error,
                video_duration: 0,
                video_percent: "0%",
                video_provider: "youtube",
                error_type: getErrorTypes(event.data),
            });
            eventsPushed[VIDEO.error] = true;
        }
    }

    function getErrorTypes(errorNo) {
        switch (errorNo) {
            case 2: return "invalid video ID";
            case 5: return "browser does not support video format";
            case 100: return "video not found";
            case 101:
            case 150: return "restricted video";
            default: return "an error occurred";
        }
    }
}
